import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAvailableTimeSlots, isTimeDuringOpeningHours, getNextAvailableTime } from '../utils/openingHours';
import './PreorderTimePicker.css';

const PreorderTimePicker = ({ restaurantOrStore, value, onChange, onClose }) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(() => {
    if (value) {
      return new Date(value);
    }
    return getNextAvailableTime(restaurantOrStore);
  });
  const [selectedTime, setSelectedTime] = useState(() => {
    if (value) {
      const date = new Date(value);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    }
    const nextTime = getNextAvailableTime(restaurantOrStore);
    return `${nextTime.getHours().toString().padStart(2, '0')}:${nextTime.getMinutes().toString().padStart(2, '0')}`;
  });

  const [availableSlots, setAvailableSlots] = useState([]);

  useEffect(() => {
    const slots = getAvailableTimeSlots(restaurantOrStore, selectedDate);
    setAvailableSlots(slots);
    
    // If selected time is not in available slots, select first available
    if (slots.length > 0) {
      const [hour, minute] = selectedTime.split(':').map(Number);
      const selectedDateTime = new Date(selectedDate);
      selectedDateTime.setHours(hour, minute, 0, 0);
      
      const isAvailable = slots.some(slot => 
        slot.getHours() === hour && slot.getMinutes() === minute
      );
      
      if (!isAvailable) {
        const firstSlot = slots[0];
        setSelectedTime(`${firstSlot.getHours().toString().padStart(2, '0')}:${firstSlot.getMinutes().toString().padStart(2, '0')}`);
      }
    }
  }, [selectedDate, restaurantOrStore, selectedTime]);

  const handleDateChange = (e) => {
    const newDate = new Date(e.target.value);
    newDate.setHours(0, 0, 0, 0);
    setSelectedDate(newDate);
  };

  const handleTimeChange = (e) => {
    setSelectedTime(e.target.value);
  };

  const handleConfirm = () => {
    const [hour, minute] = selectedTime.split(':').map(Number);
    const scheduledDateTime = new Date(selectedDate);
    scheduledDateTime.setHours(hour, minute, 0, 0);
    
    if (isTimeDuringOpeningHours(restaurantOrStore, scheduledDateTime)) {
      onChange(scheduledDateTime);
      if (onClose) onClose();
    } else {
      alert(t('cart.invalidTime') || 'Selected time is not during opening hours');
    }
  };

  const formatDate = (date) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return t('cart.today') || 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return t('cart.tomorrow') || 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    }
  };

  const minDate = new Date().toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 7);
  const maxDateString = maxDate.toISOString().split('T')[0];

  return (
    <div className="preorder-time-picker-overlay" onClick={onClose}>
      <div className="preorder-time-picker" onClick={(e) => e.stopPropagation()}>
        <div className="preorder-time-picker-header">
          <h3>{t('cart.selectPreorderTime') || 'Select Preorder Time'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        
        <div className="preorder-time-picker-content">
          <div className="date-selection">
            <label>{t('cart.selectDate') || 'Select Date'}:</label>
            <input
              type="date"
              value={selectedDate.toISOString().split('T')[0]}
              onChange={handleDateChange}
              min={minDate}
              max={maxDateString}
            />
            <span className="date-display">{formatDate(selectedDate)}</span>
          </div>

          <div className="time-selection">
            <label>{t('cart.selectTime') || 'Select Time'}:</label>
            {availableSlots.length > 0 ? (
              <div className="time-slots">
                {availableSlots.map((slot, index) => {
                  const timeString = `${slot.getHours().toString().padStart(2, '0')}:${slot.getMinutes().toString().padStart(2, '0')}`;
                  const isSelected = timeString === selectedTime;
                  return (
                    <button
                      key={index}
                      className={`time-slot ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedTime(timeString)}
                    >
                      {timeString}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="no-slots">
                {t('cart.noAvailableTimes') || 'No available times for this date'}
              </div>
            )}
          </div>

          <div className="selected-time-display">
            <strong>{t('cart.selectedTime') || 'Selected Time'}:</strong>
            <span>
              {formatDate(selectedDate)} at {selectedTime}
            </span>
          </div>
        </div>

        <div className="preorder-time-picker-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            {t('cart.cancel') || 'Cancel'}
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleConfirm}
            disabled={availableSlots.length === 0}
          >
            {t('cart.confirm') || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreorderTimePicker;
